import React, {useEffect, useState} from 'react'
import PropTypes from 'prop-types'
import {VStack, Image} from '@salesforce/retail-react-app/app/components/shared/ui'
import {getAssetUrl} from '@salesforce/pwa-kit-react-sdk/ssr/universal/utils'
import {isGoogleMapsLibraryLoaded} from '@app/components/google-maps'
import GooglePlacesAccessibility from '../../components/google-places-accessibility'

/**
 * GooglePlacesSuggestions is a React functional component that provides location-based autocomplete suggestions
 * using the Google Places API. It listens for changes in the input prop and fetches relevant place predictions
 * when the input is non-empty and the Google Maps Places library is loaded. Suggestions are displayed in a list,
 * and clicking on a suggestion triggers the onSuggestionClick callback with the selected suggestion object.
 * The component also displays a "Powered by Google" image to indicate the source of the data.
 *
 * Props:
 * @param {string} input - The current user input to be used for fetching place suggestions.
 * @param {function} onSuggestionClick - Callback function invoked when a suggestion is selected. Receives the selected suggestion as an argument.
 *
 * State:
 * @state {Array} suggestions - An array of suggestion objects returned from the Google Places API.
 *
 * Usage:
 * Render this component within a parent component, passing the current input value and a handler for suggestion selection.
 *
 * Dependencies:
 * - Requires the Google Maps JavaScript API with the Places library loaded.
 * - Utilizes external helper functions: isGoogleMapsLibraryLoaded and getAssetUrl.
 * - Uses Chakra UI's VStack and Image components for layout and styling.
 */
const GooglePlacesSuggestions = ({
    input,
    onSuggestionClick,
    instructionsId,
    resultsId,
    onOpenChange,
    inputRef,
    onActiveDescendantChange
}) => {
    const [suggestions, setSuggestions] = useState([])
    const [resultsCount, setResultsCount] = useState(null)
    const [activeSuggestionIndex, setActiveSuggestionIndex] = useState(-1)

    useEffect(() => {
        onOpenChange(suggestions.length > 0)
    }, [suggestions.length])

    const selectSuggestion = (suggestion) => {
        setSuggestions([])
        setResultsCount(null)
        setActiveSuggestionIndex(-1)
        onSuggestionClick(suggestion)
    }

    useEffect(() => {
        const inputElement = inputRef.current
        if (!inputElement) return

        const handleKeyDown = (event) => {
            if (!suggestions.length) return

            if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
                event.preventDefault()
                setActiveSuggestionIndex((currentIndex) => {
                    if (event.key === 'ArrowDown') {
                        return (currentIndex + 1) % suggestions.length
                    }
                    return currentIndex <= 0 ? suggestions.length - 1 : currentIndex - 1
                })
            } else if (
                event.key === 'Enter' &&
                activeSuggestionIndex >= 0 &&
                activeSuggestionIndex < suggestions.length
            ) {
                event.preventDefault()
                selectSuggestion(suggestions[activeSuggestionIndex])
            } else if (event.key === 'Escape') {
                setSuggestions([])
                setResultsCount(null)
                setActiveSuggestionIndex(-1)
            }
        }

        inputElement.addEventListener('keydown', handleKeyDown)
        return () => inputElement.removeEventListener('keydown', handleKeyDown)
    }, [suggestions, activeSuggestionIndex, inputRef])

    useEffect(() => {
        const activeId =
            activeSuggestionIndex >= 0 && activeSuggestionIndex < suggestions.length
                ? `${resultsId}-result-${activeSuggestionIndex}`
                : null
        onActiveDescendantChange(activeId)
    }, [activeSuggestionIndex, suggestions.length, resultsId, onActiveDescendantChange])

    useEffect(() => {
        let cancelled = false
        const fetchSuggestions = async () => {
            if (input === '') {
                setSuggestions([])
                setResultsCount(null)
                setActiveSuggestionIndex(-1)
                return
            }

            setActiveSuggestionIndex(-1)
            if (isGoogleMapsLibraryLoaded('places') && input) {
                const sessionToken = new window.google.maps.places.AutocompleteSessionToken()
                const request = {
                    input,
                    sessionToken: sessionToken
                }

                try {
                    const {suggestions} =
                        await window.google.maps.places.AutocompleteSuggestion.fetchAutocompleteSuggestions(
                            request
                        )

                    if (cancelled) return
                    setSuggestions(suggestions)
                    setResultsCount(suggestions.length)
                    setActiveSuggestionIndex(-1)
                } catch (error) {
                    console.error('Error fetching place predictions:', error)
                }
            }
        }

        fetchSuggestions()
        return () => {
            cancelled = true
        }
    }, [input])

    return (
        <>
            <GooglePlacesAccessibility
                instructionsId={instructionsId}
                resultsCount={resultsCount}
            />
            <VStack className="googleplaces-suggestions" alignItems="flex-start" pt={5} pb={12}>
                <ul
                    id={resultsId}
                    role="listbox"
                    hidden={suggestions.length === 0}
                    className="googleplaces-list"
                >
                    {suggestions.map((suggestion, index) => (
                        <li
                            key={index}
                            id={`${resultsId}-result-${index}`}
                            role="option"
                            aria-selected={activeSuggestionIndex === index}
                            className={`googleplaces-item${
                                activeSuggestionIndex === index
                                    ? ' googleplaces-item-active'
                                    : ''
                            }`}
                            onClick={() => selectSuggestion(suggestion)}
                        >
                            <span className="googleplaces-option">
                                {suggestion.placePrediction.text.toString()}
                            </span>
                        </li>
                    ))}
                </ul>
                <Image
                    width={59}
                    src={getAssetUrl('static/img/google_on_white.png')}
                    alt="Powered by Google"
                />
            </VStack>
        </>
    )
}

GooglePlacesSuggestions.propTypes = {
    input: PropTypes.string.isRequired,
    onSuggestionClick: PropTypes.func.isRequired,
    instructionsId: PropTypes.string.isRequired,
    resultsId: PropTypes.string.isRequired,
    onOpenChange: PropTypes.func.isRequired,
    inputRef: PropTypes.object.isRequired,
    onActiveDescendantChange: PropTypes.func.isRequired
}

export default GooglePlacesSuggestions
